import { useAuth, useInboxConversation, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { withKey } from "@tellescope/ui";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInboxThreadDraft } from "../../hooks";
import { useInboxMessageDeleteMutations } from "../../../../hooks";
import { pendo } from "../../../../tracking";
import {
  MessageCompositionFooter,
  MessageCompositionFooterPayload,
} from "../../../../components/MessageCompositionFooter";
import { MentionDropdown } from "../../../../components/MentionDropdown";
import { MentionsBadgeRow } from "../../../../components/MentionsBadgeRow";
import { useMentions } from "../../../../components/useMentions";

export interface InboxThreadChatCompositionFooterProps {
  threadId: string;
  conversationData?: ReturnType<typeof useInboxConversation>;
}

export const InboxThreadChatCompositionFooter = withKey(
  ({ threadId, conversationData }: InboxThreadChatCompositionFooterProps) => {
    const { t } = useTranslation("inbox");
    const { currentUser } = useAuth();
    const { deleteChatMutation } = useInboxMessageDeleteMutations();
    const [composerKey, setComposerKey] = useState(0);

    const internalConversationData = useInboxConversation(threadId, { enabled: !conversationData });
    const resolvedConversationData = conversationData ?? internalConversationData;
    const { metadata, drafts } = resolvedConversationData;

    const mentions = useMentions();

    const { queryParams, setQueryParams, matchingDraft, isEditingScheduled, sendAtInPast, shouldQueryDraft } =
      useInboxThreadDraft({ conversationData: resolvedConversationData });

    const draftQuery = useTellescopeQuery({
      model: "chats",
      method: "getOne",
      request: queryParams.draftId ?? "",
      enabled: shouldQueryDraft,
    });

    const latestDraft = useMemo(() => {
      if (queryParams.draftId) {
        return matchingDraft?.content ?? draftQuery.data?.message;
      }
      return drafts.at(0)?.content;
    }, [draftQuery.data, drafts, matchingDraft, queryParams.draftId]);

    const buildSendParams = ({
      message,
      attachments,
      mentionIds,
    }: MessageCompositionFooterPayload & { mentionIds: string[] }) => ({
      attachments: attachments.map(({ type, name, secureName }) => ({ type, name: name ?? "", secureName })),
      roomId: metadata?.threadId ?? "",
      userId: currentUser.id,
      linkOpenTrackingIds: [],
      message,
      ...(mentionIds.length > 0 ? { mentions: mentionIds } : {}),
    });

    const applyMentionAssignees = () => {
      const { mentions: mentionIds } = mentions.buildMentionPayload();
      const existingAssignees = metadata?.assignedTo ?? [];
      const newAssignees = mentionIds.filter((id) => !existingAssignees.includes(id));
      const targetAssignees = newAssignees.length > 0 ? [...existingAssignees, ...newAssignees] : null;
      return { mentionIds, targetAssignees };
    };

    const onMutationSuccess = () => {
      setComposerKey((k) => k + 1);
      setQueryParams((prev) => ({ ...prev, draftId: undefined }));
      mentions.reset();
    };

    const sendMessageMutation = useTellescopeMutation({
      model: "chats",
      method: "createOne",
      onSuccess: onMutationSuccess,
    });

    const updateMessageMutation = useTellescopeMutation({
      model: "chats",
      method: "updateOne",
      onSuccess: onMutationSuccess,
    });

    const canSend = !!metadata?.threadId && !sendMessageMutation.isPending;

    return (
      <div className="relative">
        {mentions.mentionState.isOpen && (
          <MentionDropdown
            query={mentions.mentionState.query}
            onSelect={(target) => {
              mentions.onMentionSelect(target, mentions.textareaRef.current?.value ?? "");
            }}
            onDismiss={mentions.onMentionDismiss}
          />
        )}
        <MentionsBadgeRow
          pendingMentions={mentions.pendingMentions}
          existingUserIds={metadata?.assignedTo ?? []}
          onRemove={mentions.onMentionRemove}
        />
        <MessageCompositionFooter
          key={composerKey}
          placeholder={t("Type your reply...")}
          autoFocus
          submitOnEnter={!isEditingScheduled}
          initialMessage={latestDraft}
          canSend={canSend}
          isUpdatePending={updateMessageMutation.isPending}
          enduserId={metadata?.enduserIds.at(0)}
          channelType="chat"
          isEditingScheduled={isEditingScheduled}
          sendAtInPast={sendAtInPast}
          initDate={matchingDraft?.sendAt}
          scheduleDialogKey={matchingDraft?.sendAt?.toISOString() ?? "no-init-date"}
          ref={mentions.textareaRef}
          onMessageChange={mentions.handleMessageChange}
          onSubmit={async (payload) => {
            pendo.track("chat-thread-compose-send");
            const { mentionIds, targetAssignees } = applyMentionAssignees();
            const draftToDelete = matchingDraft;
            await sendMessageMutation.mutateAsync(buildSendParams({ ...payload, mentionIds }));
            if (draftToDelete) deleteChatMutation.mutate(draftToDelete.id);
            if (targetAssignees) {
              await resolvedConversationData.setAssignees(targetAssignees);
            }
          }}
          onSchedule={async (payload, sendAt) => {
            if (matchingDraft) {
              updateMessageMutation.mutate({ id: matchingDraft.id, sendAt, message: payload.message });
            } else if (metadata?.threadId) {
              const { mentionIds, targetAssignees } = applyMentionAssignees();
              await sendMessageMutation.mutateAsync({ ...buildSendParams({ ...payload, mentionIds }), sendAt });
              if (targetAssignees) {
                await resolvedConversationData.setAssignees(targetAssignees);
              }
            }
          }}
          onSendNow={({ message }) => {
            if (!matchingDraft) return;
            updateMessageMutation.mutate({ id: matchingDraft.id, message, sendAt: new Date() });
          }}
          onSaveScheduled={({ message }) => {
            if (!matchingDraft) return;
            updateMessageMutation.mutate({ id: matchingDraft.id, message });
          }}
          onReschedule={({ message }, sendAt) => {
            if (!matchingDraft) return;
            updateMessageMutation.mutate({ id: matchingDraft.id, sendAt, message });
          }}
          trackingIds={{
            schedule: "chat-thread-schedule-message-dialog-open",
            reschedule: "chat-thread-reschedule-message-dialog-open",
          }}
        />
      </div>
    );
  },
  "threadId"
);
